import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { LinearProgress, Divider } from "@mui/material";

import { api } from "../../../services/api/ApiService";
import { OrganizationSubscriptionPlansStorageModel } from "../../../services/organization-subscription-plans/OrganizationSubscriptionPlansStorage";
import Checkbox from "../../../components/checkbox/Checkbox";
import Svg from "../../../components/svg/Svg";
import {
  CurrentPlan,
  AddNew,
  UsersAndSeats,
  Success,
  StorageSpace,
  VideoAnalysis,
  Warning,
} from "../../../components/svg/svgImageList";
import { translationService } from "../../../services/translation/TranslationService";
import "./composition.scss";
import {
  toRemainingPercentage,
  normalise,
  formatBytes,
  teraToBytes,
  getLocalPrice,
  formatNumberBasedOnLanguage,
} from "../../../helpers/MathHelpers";
import { SubscriptionPlan } from "../../../services/organization-subscription-plans/SubscriptionPlan";
import { TermsAndConditions } from "../../../components/terms-and-conditions/TermsAndConditions";

interface ActiveSubscriptionPlanCardProps {
  plan: SubscriptionPlan;
  hasFreeSubscription: boolean;
  totalFullRightsSeats: number;
  totalGuestSeats: number;
  totalStorageSize: number;
  totalVideoAnalysisCalls: number;
}

export const ActiveSubscriptionPlanCard = observer((props: ActiveSubscriptionPlanCardProps) => {
  const { plan, totalFullRightsSeats, totalGuestSeats, hasFreeSubscription } = props;

  const getExtraVideoAnalysisCallsLabel = useCallback(() => {
    const totalExtraVACalls =
      (plan.extraVACalls?.activeSubscriptionPackageAmount || 0) *
      (plan.extraVACalls?.videoAnalyserCalls || 0);

    if (!plan.isPaidSubscription) return 0;

    if (plan.extraVACalls?.activeSubscriptionPackageAmount === 1)
      return translationService.translate("PlanUsage.extra-video-1-package", {
        totalCalls: totalExtraVACalls,
      });

    return translationService.translate("PlanUsage.extra-video-multiple-packages", {
      noOfPackages: plan.extraVACalls?.activeSubscriptionPackageAmount || 0,
      totalCalls: totalExtraVACalls,
    });
  }, [plan.extraVACalls, plan.isPaidSubscription]);

  const planStorageSpaceFormat = useMemo(() => {
    return formatBytes(teraToBytes(plan.storageSize), 0);
  }, [plan.storageSize]);

  return (
    <div className="subscription-usage-detail-card">
      <div className="card-title">
        {translationService.translate("PlanUsage.current-plan", {
          currentPlan: translationService.translate(`SubscriptionPlans.card.${plan.name}`),
        })}
      </div>
      <div className="svg-container">
        <Svg svg={CurrentPlan} height={80} />
      </div>
      <div className="included-benefits-container">
        <div className="title">
          <Checkbox className="large-checkbox" checked readOnly labelKey="PlanUsage.included-in-plan" />
        </div>
        <div className="included-benefits">
          <div className="benefit-container">
            <div className="large-label">
              {totalFullRightsSeats && totalFullRightsSeats > 1
                ? totalFullRightsSeats +
                translationService.translate("SubscriptionPlans.card.seats")
                : totalFullRightsSeats +
                translationService.translate("SubscriptionPlans.card.seat")}
            </div>
            <Divider sx={{ borderBottomWidth: 2 }} style={{ margin: "10px 0" }} />
            <div className="small-label">
              {translationService.translate("PlanUsage.for-members")}
            </div>
          </div>
          <div className="benefit-container">
            <div className="large-label">
              {totalGuestSeats
                ? `${totalGuestSeats} ${translationService.translate(
                  "SubscriptionPlans.card.view-rights"
                )}`
                : translationService.translate("PlanUsage.unlimited-guest-seats")}
            </div>
            <Divider sx={{ borderBottomWidth: 2 }} style={{ margin: "10px 0" }} />
            <div className="small-label">
              {translationService.translate("PlanUsage.with-view-rights")}
            </div>
          </div>
        </div>
        <div className="included-benefits">
          <div className="benefit-container">
            <div className="large-label">{`${planStorageSpaceFormat.amount} ${planStorageSpaceFormat.unit}`}</div>
            <Divider sx={{ borderBottomWidth: 2 }} style={{ margin: "10px 0" }} />
            <div className="small-label">
              {translationService.translate("SubscriptionPlans.card.storage")}
            </div>
          </div>
          <div className="benefit-container">
            <div className="large-label">
              {`${plan.videoAnalyserCalls} ${translationService.translate("general.video")}`}
            </div>
            <Divider sx={{ borderBottomWidth: 2 }} style={{ margin: "10px 0" }} />
            <div className="small-label">
              {translationService.translate("SubscriptionPlans.card.analysis")}
            </div>
          </div>
        </div>
      </div>
      <div className={`extra-packages-container ${hasFreeSubscription && "disabled"}`}>
        <div className="title">
          <Svg className="icon-add-new-light" svg={AddNew} width={50} height={50} />
          <span>{translationService.translate("PlanUsage.extra-packages.title")}</span>
        </div>
        <div className="packages-container">
          <div className="benefit-container">
            <div className="large-label">
              {`${plan.extraStoragePackage?.activeSubscriptionPackageAmount || 0
                } ${translationService.translate("SubscriptionPlans.card.TB")}`}
            </div>
            <Divider sx={{ borderBottomWidth: 2 }} style={{ margin: "10px 0" }} />
            <div className="small-label">
              {translationService.translate("SubscriptionPlans.card.extra-storage")}
            </div>
          </div>
          <div className="benefit-container">
            <div className="large-label">{getExtraVideoAnalysisCallsLabel()}</div>
            <Divider sx={{ borderBottomWidth: 2 }} style={{ margin: "10px 0" }} />
            <div className="small-label">
              {translationService.translate("SubscriptionPlans.card.extra-video-analysis")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

interface UsersAndSeatsUsageProps {
  totalFullRightsSeats: number;
  usedFullRightsSeats: number;
  totalGuestSeats: number;
  usedGuestSeats: number;
}

export const UsersAndSeatsUsage = observer((props: UsersAndSeatsUsageProps) => {
  const { totalFullRightsSeats, usedFullRightsSeats, totalGuestSeats, usedGuestSeats } = props;

  const isNumberOfGuestSeatsUnlimited = useMemo(() => {
    return totalGuestSeats === 0;
  }, [totalGuestSeats]);

  return (
    <div className="subscription-usage-detail-card">
      <div className="card-title">{translationService.translate("PlanUsage.users-and-seats")}</div>
      <div className="svg-container">
        <Svg svg={UsersAndSeats} height={80} />
      </div>
      <div className="full-rights-seats-container">
        <div className="title">
          <div className="number-container">{totalFullRightsSeats}</div>
          {translationService.translate("PlanUsage.total-full-rights-seats")}
        </div>
        <LinearProgress
          variant="determinate"
          value={normalise(usedFullRightsSeats, totalFullRightsSeats)}
        />
        <div className="details-container">
          <div className="detail used">
            <div className="number-container">{usedFullRightsSeats}</div>
            {translationService.translate("PlanUsage.used-full-rights-seats")}
          </div>
          <div className="detail">
            <div className="number-container">
              {Math.max(0, totalFullRightsSeats - usedFullRightsSeats)}
            </div>
            {translationService.translate("PlanUsage.remaining-full-rights-seats")}
          </div>
        </div>
      </div>
      <div className="guest-seats-container">
        <div className="title">
          <div className="number-container">
            {totalGuestSeats || translationService.translate("PlanUsage.unlimited-guest-seats")}
          </div>
          {translationService.translate("PlanUsage.total-guest-seats")}
        </div>
        {!isNumberOfGuestSeatsUnlimited && (
          <>
            <LinearProgress
              variant="determinate"
              value={normalise(usedGuestSeats, totalGuestSeats)}
            />
            <div className="details-container">
              <div className="detail used">
                <div className="number-container">{usedGuestSeats}</div>
                {translationService.translate("PlanUsage.used-guest-seats")}
              </div>
              <div className="detail">
                <div className="number-container">
                  {Math.max(0, totalGuestSeats - usedGuestSeats)}
                </div>
                {translationService.translate("PlanUsage.remaining-guest-seats")}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

interface StorageSpaceUsageProps {
  totalStorageSpace: number;
  usedStorageSpace: number;
}

export const StorageSpaceUsage = observer((props: StorageSpaceUsageProps) => {
  const { totalStorageSpace, usedStorageSpace } = props;

  const getRemainingPercentageComponent = useCallback(() => {
    const remainingPercentage = Math.max(
      0,
      toRemainingPercentage(usedStorageSpace, totalStorageSpace)
    );

    const shouldShowWarning = remainingPercentage <= 10;

    const svgToDisplay = shouldShowWarning ? (
      <Svg className="icon-warning" svg={Warning} width={20} height={20} />
    ) : (
      <Svg className="icon-success" svg={Success} width={20} height={20} />
    );

    const textToDisplay = shouldShowWarning ? (
      <>
        {translationService.translate("PlanUsage.remaining-storage-space-percentage-text-warning")}

        {
          <Link to="/subscription/plans">
            {translationService.translate("general.subscription-plans")}
          </Link>
        }
        {translationService.translate("PlanUsage.help.text-part-2")}
      </>
    ) : (
      translationService.translate("PlanUsage.remaining-storage-space-percentage-text-info")
    );

    return (
      <div className="remaining-percentage">
        <div className="title">
          {svgToDisplay}
          {translationService.translate("PlanUsage.remaining-storage-space-percentage", {
            remainingPercentage: remainingPercentage,
          })}
        </div>
        <div className="text">{textToDisplay}</div>
      </div>
    );
  }, [totalStorageSpace, usedStorageSpace]);

  const totalStorageSpaceFormat = useMemo(() => {
    return formatBytes(totalStorageSpace, 0);
  }, [totalStorageSpace]);

  const usedStorageSpaceFormat = useMemo(() => {
    if (usedStorageSpace === 0) return { amount: 0, unit: totalStorageSpaceFormat.unit };

    return formatBytes(usedStorageSpace);
  }, [totalStorageSpaceFormat.unit, usedStorageSpace]);

  return (
    <div className="subscription-usage-detail-card">
      <div className="card-title">{translationService.translate("PlanUsage.storage-space")}</div>
      <div className="svg-container">
        <Svg svg={StorageSpace} height={80} />
      </div>
      <div className="storage-space-container">
        <div className="title">
          <div className="number-container">{totalStorageSpaceFormat.amount}</div>
          {translationService.translate("PlanUsage.total-storage-space", {
            unit: totalStorageSpaceFormat.unit,
          })}
        </div>
        <LinearProgress
          variant="determinate"
          value={normalise(usedStorageSpace, totalStorageSpace)}
        />
        <div className="details-container">
          <div className="detail used">
            <div className="number-container">
              {formatNumberBasedOnLanguage(usedStorageSpaceFormat.amount.toString())}
            </div>
            {translationService.translate("PlanUsage.used-storage-space", {
              usedStorageSpaceUnit: usedStorageSpaceFormat.unit,
              totalStorageSpace: totalStorageSpaceFormat.amount,
              totalStorageSpaceUnit: totalStorageSpaceFormat.unit,
            })}
          </div>
        </div>
      </div>
      {getRemainingPercentageComponent()}
    </div>
  );
});

interface VideoAnalysisUsageProps {
  totalVideoAnalysisCalls: number;
  usedVideoAnalysisCalls: number;
  hasFreeSubscription: boolean;
}

export const VideoAnalysisUsage = observer((props: VideoAnalysisUsageProps) => {
  const { totalVideoAnalysisCalls, usedVideoAnalysisCalls, hasFreeSubscription } = props;

  const [organizationSubscriptionPlansStorage] = useState(() =>
    OrganizationSubscriptionPlansStorageModel.create({ autoLoad: false }, { api })
  );

  useEffect(() => {
    organizationSubscriptionPlansStorage.getExtraVideoAnalysisUnitPrice();
  }, [organizationSubscriptionPlansStorage]);

  return (
    <div className="subscription-usage-detail-card">
      <div className="card-title">{translationService.translate("PlanUsage.video-analysis")}</div>
      <div className="svg-container">
        <Svg svg={VideoAnalysis} height={80} />
      </div>
      <div className="video-analysis-container">
        <div className="title">
          <div className="number-container">{totalVideoAnalysisCalls}</div>
          {translationService.translate("PlanUsage.total-video-analysis")}
        </div>
        <LinearProgress
          variant="determinate"
          value={normalise(usedVideoAnalysisCalls, totalVideoAnalysisCalls)}
        />
        <div className="details-container">
          <div className="detail used">
            <div className="number-container">{usedVideoAnalysisCalls}</div>
            {translationService.translate("PlanUsage.used-video-analysis")}
          </div>
          <div className="detail">
            <div className="number-container">
              {Math.max(totalVideoAnalysisCalls - usedVideoAnalysisCalls, 0)}
            </div>
            {translationService.translate("PlanUsage.remaining-video-analysis")}
          </div>
        </div>
      </div>
      <div className={`extra-video-analysis-container ${hasFreeSubscription && "disabled"}`}>
        <div className="title">
          <div className="number-container">
            {Math.max(usedVideoAnalysisCalls - totalVideoAnalysisCalls, 0)}
          </div>
          {translationService.translate("PlanUsage.extra-video-analysis")}
        </div>
        <div className="text">
          {translationService.translate("PlanUsage.extra-video-analysis-text", {
            price: getLocalPrice(organizationSubscriptionPlansStorage.extraVideoAnalysisUnitPrice)
          })}
        </div>
      </div>
    </div>
  );
});

export const ContactHelp = observer(() => {
  return (
    <>
      <div className="extra-large-label">
        {translationService.translate("PlanUsage.help.title")}
      </div>
      <div>
        <div className="base-label">
          {translationService.translate("PlanUsage.help.text-part-1")}
          <Link to="/help">{translationService.translate("HelpPage.title")}</Link>
          {translationService.translate("PlanUsage.help.text-part-2")}
        </div>
        <TermsAndConditions />
      </div>
    </>
  );
});
